import * as d3 from 'd3';
import { select } from 'd3-selection';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
  monthsShort: [
    'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
    'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
  ],
  meridiem: (hour, minute, isLowercase) => (hour >= 12 ? 'p.m.' : 'a.m.'),
});

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ],
  shortMonths: [
    'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
    'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
  ],
});

export default function (props, container, isMobile) {
  const { initialWidth, initialHeight } = props;

  const margin = {
    top: 20, right: 90, bottom: 30, left: 30,
  };
  const width = initialWidth - margin.left - margin.right;
  const height = initialHeight - margin.top - margin.bottom;

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', initialHeight)
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  const x = d3.scaleTime().range([0, width]);
  const y = d3.scaleLinear().range([height, 0]);

  const line = d3.line()
    .defined(d => d.value !== null)
    .x(d => x(d.year))
    .y(d => y(d.value));

  svg.append('g')
    .attr('class', 'x-axis grid')
    .attr('transform', `translate(0,${height})`);

  const yAxis = d3.axisLeft(y)
    .ticks(4)
    .tickSize(-width)
    .tickFormat(d => d);

  svg.append('g')
    .attr('class', 'y-axis grid')
    .call(yAxis)
    .selectAll('.tick text')
    .attr('class', 'axis-label'); // Add class for styling text

  const tooltip = d3.select('body').append('div')
    .attr('class', 'tooltip')
    .style('position', 'absolute')
    .style('background', '#ffffff')
    .style('padding', '5px 10px')
    .style('border', '1px solid #d3d3d3')
    .style('border-radius', '3px')
    .style('pointer-events', 'none')
    .style('opacity', 0);

  const TRANSITION_DURATION = 750; // Synchronized transition duration


  d3.json('https://ix.cnn.io/data/birth-rates/fertility_rates_2016_2024.json')
    .then((data) => {
      // Check if the headline and timestamp elements already exist
      // const parseDate = d3.timeParse('%Y-%m-%d');
      const parseYear = d3.timeParse('%Y'); // Parse year into a date object
      const filterDate = parseYear('2016');
      const formatYear = d3.timeFormat('%Y'); // Format for tooltip display


      const nationalData = data.national_data
        .map(d => ({
          year: parseYear(d.year),
          value: d.fertility_rate,
        }))
        .filter(d => d.year >= filterDate && d.value !== null)
        .sort((a, b) => d3.ascending(a.year, b.year));

      const stateData = data.state_data
        .map(d => ({
          year: parseYear(d.year),
          value: d.fertility_rate,
          state: d.state,
          state_abbr: d.state_abbr, // add state abbreviations later
        }))
        .filter(d => d.year >= filterDate && d.value !== null)
        .sort((a, b) => d3.ascending(a.year, b.year));


      // Populate the select menu with states
      const states = Array.from(new Set(stateData.map(d => d.state)));


      const stateSelect = d3.select('#state-select'); // originally was just select but only worked to change data with d3 select
      states.forEach((state) => {
        stateSelect.append('option')
          .attr('value', state)
          .text(state);
      });

      const initialState = 'California';
      stateSelect.property('value', initialState);


      // generate dynamic dek
      const maxYear = '2024';
      const recentStateData = stateData.find(d => formatYear(d.year) === maxYear && d.state === initialState);
      const recentNatData = nationalData.find(d => formatYear(d.year) === maxYear);

      if (recentStateData && recentNatData) {
        const isLower = recentStateData.value < recentNatData.value;
        const compareText = isLower ? 'lower' : 'higher';

        if (!d3.select('.subhed.dek span').empty()) {
          d3.select('.subhed.dek span').html(`<b style="color: #CC46B2;">${initialState}</b> had a ${compareText} fertility rate than the United states in ${maxYear}. The US national average has decreased 13% in the last nine years.<br><br><b style="font-weight:bold">Births per 1,000 women, ages 15-44, 2024</b>`);
        } else {
          d3.select('.subhed.dek').append('span').html(`<b style="color: #CC46B2; font-weight:bold">${initialState}</b> had a ${compareText} fertility rate than the United States in ${maxYear}. The US national average has decreased 12% in the last eight years.<br><br><b style="font-weight:bold">Births per 1,000 women, ages 15-44, 2024</b>`);
        }
      }

      function updateText() {
        const maxYear = 2024;
        const recentStateData = stateData.find(d => d.year === maxYear && d.state === initialState);
        const recentNatData = nationalData.find(d => d.year === maxYear);

        if (recentStateData && recentNatData) {
          const isLower = recentStateData.value < recentNatData.value;
          const compareText = isLower ? 'lower' : 'higher';

          if (!d3.select('.subhed.dek span').empty()) {
            d3.select('.subhed.dek span').html(`<b>${initialState}</b> has a ${compareText} fertility rate than the national rate in ${maxYear}.`);
          } else {
            d3.select('.subhed.dek').append('span').html(`<b>${initialState}</b> has a ${compareText} fertility rate than the national rate in ${maxYear}.`);
          }
        }
      }

      updateText();

      // Calculate the Y-axis domain for the initial state
      function calculateMaxYValue(data) {
        // const baseYMax = 20; // don't need the base max for Y because we'll always have the natl rate and that should be the min Y height
        const nationalMax = d3.max(nationalData, d => d.value);
        const maxDataValue = d3.max(data, d => d.value);
        return maxDataValue > nationalMax ? maxDataValue + 20 : nationalMax + 20;
      }

      function calculateMinYValue(data) {
        // const baseYMax = 20; // don't need the base max for Y because we'll always have the natl rate and that should be the min Y height
        const nationalMin = d3.min(nationalData, d => d.value);
        const minDataValue = d3.min(data, d => d.value);
        return minDataValue < nationalMin ? minDataValue - 20 : nationalMin - 20;
      }


      const initialMaxYValue = calculateMaxYValue(stateData.filter(d => d.state === initialState));
      const initialMinYValue = calculateMinYValue(stateData.filter(d => d.state === initialState));
      y.domain([initialMinYValue, initialMaxYValue]);

      // Determine X-axis domain based on the national data
      x.domain(d3.extent(nationalData, d => d.year));

      const xAxis = d3.axisBottom(x)
        .ticks(isMobile ? 3 : 6)
        .tickFormat(d => dayjs(d).format('YYYY'));

      svg.select('.x-axis')
        .call(xAxis)
        .selectAll('.tick text')
        .attr('class', 'axis-label'); // Add class for styling text

      svg.select('.y-axis')
        .call(yAxis)
        .selectAll('.tick text')
        .attr('class', 'axis-label'); // Add class for styling text

      const nationalLine = svg.append('path')
        .datum(nationalData)
        .attr('class', 'national-line data')
        .attr('fill', 'none')
        .attr('stroke', '#c1c1c1')
        .attr('stroke-width', 1.5)
        .attr('d', line);

      const stateLine = svg.append('path')
        .datum(stateData.filter(d => d.state === initialState))
        .attr('class', 'state-line data')
        .attr('fill', 'none')
        .attr('stroke', '#CC46B2')
        .attr('stroke-width', 2)
        .attr('d', line);


      // Add the circle for highlighting data points
      const focus = svg.append('g')
        .append('circle')
        .style('fill', 'none')
        .attr('stroke', 'black')
        .attr('r', 4)
        .style('opacity', 0);

      svg.selectAll('.state-line, .national-line')
        .on('mouseover', function (event, d) {
          const mouseX = d3.pointer(event, this)[0];
          const year = x.invert(mouseX);
          const bisectDate = d3.bisector(d => d.year).left;

          // Find the index of the closest data point to the mouseX
          const index = bisectDate(d, year, 1);
          const prevData = d[index - 1] || d[index];
          const nextData = d[index] || prevData;

          // const selectedData = d[index - 1] || d[index];
          // this changes the tooltips so they choose whichever year is closer to the cursor
          const selectedData = Math.abs(year - prevData.year) < Math.abs(nextData.year - year)
            ? prevData
            : nextData;

          // Align the tooltip with the data point
          const tooltipX = x(selectedData.year);
          const tooltipY = y(selectedData.value);

          // Determine if we're hovering over the state or national line
          const isNationalLine = d3.select(this).classed('national-line');

          // Set the label for the tooltip based on the line
          const label = isNationalLine ? 'US fertility rate:' : 'State fertility rate:';

          // Show and position the focus circle
          focus.style('opacity', 1)
            .attr('cx', tooltipX)
            .attr('cy', tooltipY);

          // Show and position the tooltip
          tooltip.transition().duration(100).style('opacity', 1);
          tooltip.html(`${d3.timeFormat('%Y')(selectedData.year)}<br>${label} ${selectedData.value.toFixed(2)}`)
            .style('left', `${event.pageX + 10}px`)
            .style('top', `${tooltipY - 30}px`);
        })
        .on('mousemove', (event) => {
          tooltip.style('left', `${event.pageX + 10}px`)
            .style('top', `${event.pageY - 30}px`);
        })
        .on('mouseout', () => {
          tooltip.transition().duration(100).style('opacity', 0);
          focus.style('opacity', 0);
        });


      function updateChart(state) {
        const filteredStateData = stateData.filter(d => d.state === state);

        console.log(filteredStateData);

        // Calculate the new Y-axis domain based on the current state's data
        const maxYValue = calculateMaxYValue(filteredStateData);
        const minYValue = calculateMinYValue(filteredStateData);
        y.domain([minYValue, maxYValue]);
        // y.domain([0, maxYValue]);

        // Update the Y-axis
        svg.select('.y-axis')
          .transition()
          .duration(TRANSITION_DURATION)
          .call(yAxis);

        // Update the state line
        stateLine
          .datum(filteredStateData)
          .transition()
          .duration(TRANSITION_DURATION)
          .attr('d', line);

        // Update the national line to respond to the new Y-axis scale
        nationalLine
          .datum(nationalData)
          .transition()
          .duration(TRANSITION_DURATION)
          .attr('d', line);

        // Remove previous annotations and circles
        svg.selectAll('.annotation-layer, .annotation, .annotation-value, .line-end-circle').remove();

        addAnnotations(filteredStateData, nationalData);
      }

      function drawLastLabel(tgt, label, value, clr) {
        tgt.append('text')
          .attr('class', 'annotation')
          .attr('x', 8)
          .attr('y', 0)
          .attr('alignment-baseline', 'middle')
          .attr('fill', clr)
          .attr('font-size', '12px')
          .text(`${label}`);

        tgt.append('text')
          .attr('class', 'annotation-value')
          .attr('x', 8)
          .attr('y', 0)
          .attr('dy', 15)
          .attr('alignment-baseline', 'middle')
          .attr('fill', clr)
          .attr('font-size', '12px')
          .text(`${value.toFixed(2)}`);
      }

      function drawLastValue(tgt, d, clr) {
        tgt.append('circle')
          .attr('class', 'line-end-circle')
          .attr('cx', x(d.year))
          .attr('cy', y(d.value))
          .attr('r', 4)
          .attr('fill', clr);
      }


      function addAnnotations(stateData, nationalData) {
        const lastStateData = stateData[stateData.length - 1];
        const lastNationalData = nationalData[nationalData.length - 1];


        const MIN = 30;
        const distance = y(lastStateData.value) - y(lastNationalData.value);

        let stateOffset = 0;
        let nationalOffset = 0;

        if (Math.abs(distance) < MIN) {
          const shift = MIN - Math.abs(distance);
          if (distance < 0) {
            stateOffset = shift;
          } else if (distance > 0) {
            nationalOffset = shift;
          }
        }

        const STATE_CLR = '#CC46B2';
        const NATIONAL_CLR = '#7d7d7d';

        const annotationLayer = svg.append('g').attr('class', 'annotation-layer');

        const stateAnno = annotationLayer.append('g')
          .attr('transform', `translate(${x(lastStateData.year)}, ${y(lastStateData.value) - stateOffset})`);

        drawLastLabel(stateAnno, lastStateData.state_abbr, lastStateData.value, STATE_CLR);
        drawLastValue(annotationLayer, lastStateData, STATE_CLR);

        const nationalAnno = annotationLayer.append('g')
          .attr('transform', `translate(${x(lastNationalData.year)}, ${y(lastNationalData.value) - nationalOffset})`);

        drawLastLabel(nationalAnno, 'US', lastNationalData.value, NATIONAL_CLR);
        drawLastValue(annotationLayer, lastNationalData, NATIONAL_CLR);

        annotationLayer.style('opacity', 0)
          .transition()
          .delay(250)
          .duration(TRANSITION_DURATION)
          .style('opacity', 1);
      }

      updateChart(initialState);


      d3.select('#state-select').on('change', function () {
        const selectedState = d3.select(this).property('value');
        updateChart(selectedState);

        const maxYear = '2024';
        const recentStateData = stateData.find(d => formatYear(d.year) === maxYear && d.state === selectedState);
        const recentNatData = nationalData.find(d => formatYear(d.year) === maxYear);

        if (recentStateData && recentNatData) {
          const isLower = recentStateData.value < recentNatData.value;
          const compareText = isLower ? 'lower' : 'higher';

          d3.select('.subhed.dek span').remove(); // Clear existing dek
          d3.select('.subhed.dek').append('span')
            .html(`<b style="color: #CC46B2; font-weight:bold">${selectedState}</b> had a ${compareText} fertility rate than the US did in ${maxYear}. The US national average has decreased 12% in the last eight years.<br><br><b style="font-weight:bold">Births per 1,000 women, ages 15-44, 2024</b>`);
        }
      });


      const { pymChild } = window;
      pymChild.sendHeight();
    })
    .catch(error => console.error('Error fetching data:', error));
}
